import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideEyeOff, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { GoogleButtonComponent } from '../../../../components/google-button/google-button.component';
import { PartnerService } from '../../../../services/partner.service';
import {
  PARTNER_PASSWORD_MIN_LENGTH,
  passwordsMatch,
} from '../../../partner-set-password/partner-set-password.component';

/**
 * Sign-up for the partner program, like a shop's: e-mail and password, or Google.
 * The name comes next, in the partner area; the address only before the first payout.
 * Signing up accepts the Teilnahmebedingungen (named below the button).
 */
@Component({
  selector: 'app-affiliate-signup-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmSpinnerImports,
    GoogleButtonComponent,
  ],
  providers: [provideIcons({ lucideEye, lucideEyeOff, lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  templateUrl: './affiliate-signup-form.component.html',
})
export class AffiliateSignupFormComponent {
  private readonly partnerService = inject(PartnerService);

  /** Signed up with a password: the address to confirm. */
  readonly registered = output<string>();
  /** Signed up (or in) with Google: the partner area is open. */
  readonly signedIn = output<void>();

  readonly minLength = PARTNER_PASSWORD_MIN_LENGTH;
  readonly submitted = signal(false);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** The address already has a partner account: offer the login instead. */
  readonly emailTaken = signal(false);
  readonly showPassword = signal(false);

  readonly form = inject(FormBuilder).nonNullable.group(
    {
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(PARTNER_PASSWORD_MIN_LENGTH)]],
      confirm: ['', Validators.required],
    },
    { validators: passwordsMatch },
  );

  invalid(field: 'email' | 'password' | 'confirm'): boolean {
    const control = this.form.controls[field];
    if (field === 'confirm') {
      // Only once submitted: while typing the second field it never matches yet
      return this.submitted() && (control.invalid || !!this.form.errors?.['passwordsNotMatching']);
    }
    return (this.submitted() || control.touched) && control.invalid;
  }

  submit(): void {
    if (this.loading()) return;
    this.submitted.set(true);
    this.error.set(null);
    this.emailTaken.set(false);
    if (this.form.invalid) return;

    const email = this.form.getRawValue().email.trim().toLowerCase();
    this.loading.set(true);
    this.partnerService
      .register({ email, password: this.form.getRawValue().password, accept_terms: true })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.registered.emit(email);
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          const emailError = err.status === 400 ? err.error?.detail?.errors?.[0]?.message : null;
          if (emailError) {
            this.emailTaken.set(true);
            this.error.set(emailError);
          } else {
            this.error.set(
              err.status === 429
                ? 'Zu viele Versuche. Bitte warte eine Minute und versuche es erneut.'
                : 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.',
            );
          }
        },
      });
  }

  onGoogle(credential: string): void {
    this.error.set(null);
    this.emailTaken.set(false);
    this.loading.set(true);
    this.partnerService.loginWithGoogle(credential, 'register').subscribe({
      next: () => {
        this.loading.set(false);
        this.signedIn.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(
          err.status === 403
            ? err.error?.detail
            : 'Die Anmeldung mit Google ist fehlgeschlagen. Bitte versuche es erneut.',
        );
      },
    });
  }
}
